import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Gauge, Scissors, FileText, Mic, Music, Captions as CapIcon, Youtube, Download, Image as ImageIcon,
  Check, X, Loader2, Play, ArrowRight, RefreshCw, Sparkles, Clock, Film,
} from 'lucide-react';
import api, { mediaUrl, imageUrl, assetUrl } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, '0')}`;

const VOICES = [
  { id: 'onyx', label: 'Documentary Male' }, { id: 'echo', label: 'Calm Male' },
  { id: 'fable', label: 'Storyteller' }, { id: 'sage', label: 'Measured' },
  { id: 'nova', label: 'Energetic' }, { id: 'shimmer', label: 'Bright' },
  { id: 'coral', label: 'Warm' }, { id: 'ash', label: 'Articulate' }, { id: 'alloy', label: 'Neutral' },
];
const STYLES = ['cinematic', 'natural', 'serious', 'friendly', 'energetic'];
const AI_MODELS = [
  { id: 'gpt-5.4', label: 'GPT-5.4', vendor: 'OpenAI' },
  { id: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro', vendor: 'Google' },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', vendor: 'Anthropic' },
];
const IMG_PROVIDERS = [
  { id: 'nano-banana', label: 'Gemini Nano Banana', vendor: 'Google' },
  { id: 'gpt-image-1', label: 'GPT Image 1', vendor: 'OpenAI' },
];
const INTENSITY = ['keep_more', 'balanced', 'aggressive'];
const INTENSITY_LABEL = { keep_more: 'Keep more content', balanced: 'Balanced', aggressive: 'Aggressive cut' };
const LENGTHS = [3, 5, 8, 10, 12, 15];
const EDIT_TOGGLES = [
  ['remove_silence', 'Remove silence'], ['remove_repetition', 'Remove repetition'],
  ['remove_filler', 'Remove filler words'], ['remove_weak', 'Remove weak sections'],
  ['keep_facts', 'Keep important facts'], ['preserve_flow', 'Preserve story flow'],
  ['preserve_intro', 'Preserve intro'], ['preserve_conclusion', 'Preserve conclusion'],
];
const GENRES = ['cinematic_documentary', 'ambient', 'lo-fi', 'orchestral', 'electronic'];
const MOODS = ['mystery', 'cinematic', 'uplifting', 'calm'];

const STEPS = [
  { key: 'overview', label: 'Overview', icon: Gauge },
  { key: 'edit', label: 'AI Edit', icon: Scissors },
  { key: 'transcript', label: 'Transcript', icon: FileText },
  { key: 'voice', label: 'Voiceover', icon: Mic },
  { key: 'music', label: 'Music', icon: Music },
  { key: 'images', label: 'AI Images', icon: ImageIcon },
  { key: 'captions', label: 'Captions', icon: CapIcon },
  { key: 'youtube', label: 'YouTube', icon: Youtube },
  { key: 'export', label: 'Export', icon: Download },
];

const ANALYZE_STEPS = [
  'Extracting audio', 'Transcribing speech', 'Detecting scenes', 'Detecting silence',
  'Detecting repeated content', 'Understanding topic', 'Detecting important moments',
  'Analyzing audio', 'Creating video structure',
];

function Card({ children, className = '' }) {
  return <div className={`rounded-2xl border border-white/10 bg-[#12141D] p-6 ${className}`}>{children}</div>;
}
function StepHeader({ title, desc }) {
  return <div className="mb-6"><h2 className="font-heading text-2xl font-bold">{title}</h2>{desc && <p className="text-slate-400 mt-1">{desc}</p>}</div>;
}

function Analyzing({ project }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => Math.min(x + 1, ANALYZE_STEPS.length)), 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="max-w-lg mx-auto py-10">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-full border-2 border-[#FF4D00] border-t-transparent animate-spin mx-auto mb-5" />
        <h2 className="font-heading text-2xl font-bold">Analyzing video…</h2>
        <p className="text-slate-400 mt-1 font-mono text-sm">{project.title}</p>
      </div>
      <Card>
        <div className="space-y-3">
          {ANALYZE_STEPS.map((s, idx) => (
            <div key={s} className="flex items-center gap-3">
              {idx < i ? <span className="grid place-items-center w-5 h-5 rounded-full bg-emerald-500"><Check className="w-3 h-3 text-white" /></span>
                : idx === i ? <Loader2 className="w-5 h-5 text-[#FF4D00] animate-spin" />
                  : <span className="w-5 h-5 rounded-full border border-white/15" />}
              <span className={idx <= i ? 'text-slate-200' : 'text-slate-500'}>{s}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default function ProjectStudio() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [project, setProject] = useState(null);
  const [step, setStep] = useState('overview');
  const appliedIntent = useRef(false);

  // edit state
  const [target, setTarget] = useState(10);
  const [intensity, setIntensity] = useState(1);
  const [toggles, setToggles] = useState(Object.fromEntries(EDIT_TOGGLES.map(([k]) => [k, true])));
  // voice state
  const [voice, setVoice] = useState('onyx');
  const [vstyle, setVstyle] = useState('cinematic');
  const [speed, setSpeed] = useState(1.0);
  const [emotion, setEmotion] = useState(0.5);
  // ElevenLabs
  const [provider, setProvider] = useState('openai');
  const [elCfg, setElCfg] = useState({ configured: false, voices: [] });
  const [clones, setClones] = useState([]);
  const [elVoiceId, setElVoiceId] = useState('');
  const [elStab, setElStab] = useState(0.5);
  const [elSim, setElSim] = useState(0.75);
  const [cloneName, setCloneName] = useState('');
  const [cloneFile, setCloneFile] = useState(null);
  const [cloning, setCloning] = useState(false);

  const pollRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(`/projects/${id}`); setProject(data); return data; }
    catch { toast.error('Project not found'); nav('/app/projects'); }
  };

  const isBusy = (p) => !!p && (['uploaded', 'analyzing'].includes(p.status) ||
    p.edit?.status === 'generating' || p.voice?.status === 'generating' ||
    ['queued', 'rendering'].includes(p.export?.status));

  // Restartable poller: kept alive while any background job runs, and re-armed
  // whenever the user triggers a new job (edit / voice / export).
  const ensurePolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const p = await load();
      if (!isBusy(p)) { clearInterval(pollRef.current); pollRef.current = null; }
    }, 2500);
  };

  useEffect(() => {
    load().then(() => ensurePolling());
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // apply create-page intent once analysis is done
  useEffect(() => {
    if (project && project.status !== 'uploaded' && project.status !== 'analyzing' && !appliedIntent.current) {
      appliedIntent.current = true;
      try {
        const intent = JSON.parse(localStorage.getItem(`te_intent_${id}`) || 'null');
        if (intent) {
          if (intent.target) setTarget(intent.target);
        }
      } catch {}
    }
  }, [project, id]);

  useEffect(() => {
    api.get('/voice/elevenlabs/voices').then(({ data }) => {
      setElCfg(data);
      if (data.voices?.[0]) setElVoiceId((v) => v || data.voices[0].voice_id);
    }).catch(() => {});
    api.get('/voice/clones').then(({ data }) => setClones(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!project) return <div className="py-20 text-center text-slate-400 font-mono text-sm">Loading studio…</div>;
  if (project.status === 'failed')
    return (
      <Card className="max-w-lg mx-auto text-center">
        <X className="w-10 h-10 text-rose-400 mx-auto mb-3" />
        <div className="font-heading text-xl font-bold">Analysis failed</div>
        <p className="text-slate-400 mt-2 text-sm">{project.error || 'Something went wrong processing this video.'}</p>
        <Button onClick={() => nav('/app/create')} className="rounded-full mt-5">Try another video</Button>
      </Card>
    );
  if (['uploaded', 'analyzing'].includes(project.status)) return <Analyzing project={project} />;

  const a = project.analysis || {};
  const edit = project.edit || {};
  const vo = project.voice || {};
  const music = project.music || {};
  const captions = project.captions || {};
  const exp = project.export || {};

  const patch = async (path, body) => { try { await api.patch(`/projects/${id}/${path}`, body); load(); } catch (e) { toast.error('Save failed'); } };

  /* ---------- steps ---------- */
  const Overview = () => (
    <div>
      <StepHeader title="AI Video Overview" desc="Here is what the AI understood about your video." />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-0 overflow-hidden">
          <video controls src={mediaUrl(id, 'source')} className="w-full aspect-video bg-black" data-testid="source-video" />
        </Card>
        <Card>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Title</div>
          <div className="font-heading text-xl font-bold mt-1">{project.title}</div>
          <p className="text-sm text-slate-400 mt-2">{a.summary}</p>
          <div className="grid grid-cols-2 gap-3 mt-5">
            {[['Original length', fmt(a.duration)], ['Speech', fmt(a.speech_seconds)], ['Silence', fmt(a.silence_seconds)], ['Scenes', a.scenes]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-[#0B0C12] p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
                <div className="font-heading text-lg font-bold mt-0.5">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Key topics</div>
            <div className="flex flex-wrap gap-2">
              {(a.key_topics || []).map((t) => <Badge key={t} variant="outline" className="border-[#8B5CF6]/40 text-violet-200 bg-[#8B5CF6]/10">{t}</Badge>)}
            </div>
          </div>
        </Card>
      </div>
      <Card className="mt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500">AI Brain</div>
            <div className="text-sm text-slate-400 mt-1">Model that powers analysis, smart-cut editing, B-roll prompts & YouTube copy.</div>
          </div>
          <Select value={project.ai_model || 'gpt-5.4'} onValueChange={(v) => patch('ai-model', { ai_model: v })}>
            <SelectTrigger className="w-full sm:w-64 bg-[#0B0C12] border-white/12" data-testid="ai-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AI_MODELS.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`ai-model-option-${m.id}`}>
                  {m.label} <span className="text-slate-500">· {m.vendor}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>
      <div className="flex justify-end mt-6">
        <Button data-testid="to-edit-btn" onClick={() => setStep('edit')} className="rounded-full">Continue to editing <ArrowRight className="w-4 h-4 ml-1" /></Button>
      </div>
    </div>
  );

  const generateEdit = async () => {
    await api.post(`/projects/${id}/edit`, { target_minutes: target, intensity: INTENSITY[intensity], toggles });
    toast.info('AI is building your edit…');
    await load(); ensurePolling();
  };

  const EditStep = () => (
    <div>
      <StepHeader title="AI Edit" desc={`Original video: ${fmt(a.duration)}. Choose a target and let AI cut it.`} />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <div className="text-sm text-slate-400 mb-3">Target duration</div>
          <div className="grid grid-cols-3 gap-2">
            {LENGTHS.map((l) => (
              <button key={l} data-testid={`target-${l}`} onClick={() => setTarget(l)}
                className={`rounded-lg border py-2.5 text-sm font-mono transition-colors ${target === l ? 'border-[#FF4D00] bg-[#FF4D00]/10 text-white' : 'border-white/10 bg-[#0B0C12] text-slate-300 hover:border-white/25'}`}>
                {l} min
              </button>
            ))}
          </div>
          <div className="text-sm text-slate-400 mt-6 mb-2">Editing intensity — <span className="text-slate-200">{INTENSITY_LABEL[INTENSITY[intensity]]}</span></div>
          <Slider value={[intensity]} min={0} max={2} step={1} onValueChange={(v) => setIntensity(v[0])} data-testid="intensity-slider" />
          <div className="flex justify-between text-[11px] font-mono text-slate-500 mt-2"><span>Keep more</span><span>Balanced</span><span>Aggressive</span></div>
        </Card>
        <Card>
          <div className="text-sm text-slate-400 mb-3">Options</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {EDIT_TOGGLES.map(([k, l]) => (
              <button key={k} data-testid={`edit-toggle-${k}`} onClick={() => setToggles((x) => ({ ...x, [k]: !x[k] }))}
                className="flex items-center gap-2 text-left">
                <span className={`grid place-items-center w-5 h-5 rounded shrink-0 ${toggles[k] ? 'bg-[#FF4D00]' : 'bg-white/10'}`}>{toggles[k] && <Check className="w-3.5 h-3.5 text-white" />}</span>
                <span className="text-sm text-slate-300">{l}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div className="flex items-center gap-3 mt-6">
        <Button data-testid="generate-edit-btn" onClick={generateEdit} disabled={edit.status === 'generating'} className="rounded-full h-11 px-6 font-semibold">
          {edit.status === 'generating' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Edit</>}
        </Button>
      </div>

      {edit.status === 'ready' && (
        <Card className="mt-6">
          <div className="grid sm:grid-cols-2 gap-6 items-center">
            <div className="flex items-center gap-6">
              <div><div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Original</div><div className="font-heading text-4xl font-extrabold text-slate-400">{fmt(edit.original_seconds)}</div></div>
              <ArrowRight className="w-6 h-6 text-slate-600" />
              <div><div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4D00]">Final</div><div className="font-heading text-4xl font-extrabold text-gradient">{fmt(edit.final_seconds)}</div></div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {edit.removed && Object.entries(edit.removed).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-slate-400"><span className="font-mono text-slate-300">−{fmt(v)}</span>{k.replace('_', ' ')}</div>
              ))}
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button data-testid="accept-edit-btn" onClick={() => { toast.success('Edit accepted'); setStep('voice'); }} className="rounded-full">Accept Edit</Button>
            <Button variant="outline" onClick={() => setStep('transcript')} className="rounded-full border-white/15 bg-white/5 hover:bg-white/10">Review Changes</Button>
          </div>
        </Card>
      )}
    </div>
  );

  const TranscriptStep = () => {
    const [segs, setSegs] = useState(project.segments || []);
    const kept = segs.filter((s) => s.keep);
    const finalSec = kept.reduce((n, s) => n + (s.end - s.start), 0);
    const toggle = async (idx) => {
      const next = segs.map((s) => s.index === idx ? { ...s, keep: !s.keep } : s);
      setSegs(next);
      api.patch(`/projects/${id}/segments`, { segments: next.map((s) => ({ index: s.index, keep: s.keep })) }).catch(() => {});
    };
    return (
      <div>
        <StepHeader title="Transcript Editor" desc="Remove a segment and the video is cut. Text-based editing." />
        <div className="flex items-center justify-between rounded-xl border border-white/10 bg-[#12141D] px-4 py-3 mb-4 sticky top-0 z-10">
          <span className="text-sm text-slate-400">Final duration</span>
          <span className="font-heading text-lg font-bold text-gradient">{fmt(finalSec)}</span>
        </div>
        {segs.length === 0 ? (
          <Card className="text-center text-slate-400">No speech was detected in this video, so the full clip will be kept.</Card>
        ) : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {segs.map((s) => (
              <div key={s.index} data-testid={`segment-${s.index}`}
                className={`flex items-start gap-4 rounded-xl border p-4 transition-colors ${s.keep ? 'border-white/10 bg-[#12141D]' : 'border-rose-500/30 bg-rose-500/5 opacity-60'}`}>
                <span className="font-mono text-xs text-slate-500 pt-0.5 w-12 shrink-0">{fmt(s.start)}</span>
                <p className={`flex-1 text-sm ${s.keep ? 'text-slate-200' : 'text-slate-500 line-through'}`}>{s.text || <span className="italic text-slate-600">(silence)</span>}</p>
                <button data-testid={`toggle-segment-${s.index}`} onClick={() => toggle(s.index)}
                  className={`shrink-0 text-xs font-mono px-3 py-1 rounded-full border ${s.keep ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-rose-500/40 text-rose-300 bg-rose-500/10'}`}>
                  {s.keep ? 'KEEP' : 'REMOVE'}
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end mt-6"><Button onClick={() => setStep('voice')} className="rounded-full">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button></div>
      </div>
    );
  };

  const generateVoice = async () => {
    await api.post(`/projects/${id}/voice`, { voice, style: vstyle, speed, emotion });
    toast.info('Generating AI narration…');
    await load(); ensurePolling();
  };

  const generateElevenVoice = async () => {
    if (!elVoiceId) { toast.error('Pick an ElevenLabs voice'); return; }
    const label = [...(elCfg.voices || []), ...clones].find((v) => v.voice_id === elVoiceId)?.name || 'ElevenLabs';
    await api.post(`/projects/${id}/voice/elevenlabs`, { voice_id: elVoiceId, voice_label: label, stability: elStab, similarity: elSim });
    toast.info('Generating ElevenLabs narration…');
    await load(); ensurePolling();
  };

  const cloneVoice = async () => {
    if (!cloneFile || !cloneName.trim()) { toast.error('Add a name and an audio sample'); return; }
    setCloning(true);
    try {
      const fd = new FormData();
      fd.append('name', cloneName.trim());
      fd.append('files', cloneFile);
      const { data } = await api.post('/voice/clone', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setClones((c) => [...c, data]);
      setElVoiceId(data.voice_id);
      setCloneName(''); setCloneFile(null);
      toast.success('Voice cloned — it is now selectable above.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cloning failed');
    } finally { setCloning(false); }
  };

  const VoiceStep = () => (
    <div>
      <StepHeader title="Voiceover Studio" desc="Replace the original voice with a studio AI narrator, synced to your cut." />
      <div className="inline-flex rounded-full border border-white/10 bg-[#0B0C12] p-1 mb-5">
        {[['openai', 'OpenAI'], ['elevenlabs', 'ElevenLabs']].map(([k, l]) => (
          <button key={k} data-testid={`voice-provider-${k}`} onClick={() => setProvider(k)}
            className={`px-4 py-1.5 rounded-full text-sm transition-colors ${provider === k ? 'bg-[#FF4D00] text-white' : 'text-slate-400 hover:text-white'}`}>{l}</button>
        ))}
      </div>

      {provider === 'elevenlabs' && (
        <div className="space-y-4 mb-2">
          {!elCfg.configured ? (
            <Card className="border-amber-500/30">
              <div className="text-amber-200 text-sm">ElevenLabs isn't configured yet. Add your <span className="font-mono">ELEVEN_API_KEY</span> to the backend to enable multilingual voices and voice cloning.</div>
            </Card>
          ) : (
            <>
              <Card>
                <div className="text-sm text-slate-400 mb-2">ElevenLabs voice</div>
                <Select value={elVoiceId} onValueChange={setElVoiceId}>
                  <SelectTrigger className="bg-[#0B0C12] border-white/12" data-testid="eleven-voice-select"><SelectValue placeholder="Choose a voice" /></SelectTrigger>
                  <SelectContent>
                    {clones.map((v) => <SelectItem key={v.voice_id} value={v.voice_id}>⭐ {v.name} (cloned)</SelectItem>)}
                    {(elCfg.voices || []).map((v) => <SelectItem key={v.voice_id} value={v.voice_id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-sm text-slate-400 mt-4 mb-2">Stability — <span className="font-mono text-slate-200">{elStab.toFixed(2)}</span></div>
                <Slider value={[elStab]} min={0} max={1} step={0.05} onValueChange={(v) => setElStab(v[0])} data-testid="eleven-stability" />
                <div className="text-sm text-slate-400 mt-4 mb-2">Similarity — <span className="font-mono text-slate-200">{elSim.toFixed(2)}</span></div>
                <Slider value={[elSim]} min={0} max={1} step={0.05} onValueChange={(v) => setElSim(v[0])} data-testid="eleven-similarity" />
                <Button data-testid="generate-eleven-btn" onClick={generateElevenVoice} disabled={vo.status === 'generating'} className="rounded-full mt-5 font-semibold">
                  {vo.status === 'generating' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Mic className="w-4 h-4 mr-2" /> Generate with ElevenLabs</>}
                </Button>
              </Card>
              <Card>
                <div className="font-medium mb-1">Clone your voice</div>
                <div className="text-sm text-slate-500 mb-3">Upload a ~20s clean sample (mp3/wav). It becomes a selectable voice above.</div>
                <div className="grid sm:grid-cols-2 gap-3 items-center">
                  <Input value={cloneName} onChange={(e) => setCloneName(e.target.value)} placeholder="Voice name" className="bg-[#0B0C12] border-white/12" data-testid="clone-name" />
                  <input type="file" accept="audio/*" onChange={(e) => setCloneFile(e.target.files?.[0] || null)} data-testid="clone-file" className="text-sm text-slate-400" />
                </div>
                <Button data-testid="clone-voice-btn" onClick={cloneVoice} disabled={cloning} variant="outline" className="rounded-full mt-4 border-white/15 bg-white/5 hover:bg-white/10">
                  {cloning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cloning…</> : 'Clone voice'}
                </Button>
              </Card>
            </>
          )}
        </div>
      )}

      {provider === 'openai' && (<>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <div className="text-sm text-slate-400 mb-3">Voice</div>
          <div className="grid grid-cols-3 gap-2">
            {VOICES.map((v) => (
              <button key={v.id} data-testid={`voice-${v.id}`} onClick={() => setVoice(v.id)}
                className={`rounded-lg border p-3 text-left transition-colors ${voice === v.id ? 'border-[#FF4D00] bg-[#FF4D00]/10' : 'border-white/10 bg-[#0B0C12] hover:border-white/25'}`}>
                <div className="text-xs font-semibold">{v.label}</div>
                <div className="font-mono text-[10px] text-slate-500 mt-0.5">{v.id}</div>
              </button>
            ))}
          </div>
        </Card>
        <Card>
          <div className="text-sm text-slate-400 mb-3">Style</div>
          <div className="flex flex-wrap gap-2 mb-5">
            {STYLES.map((s) => (
              <button key={s} data-testid={`style-${s}`} onClick={() => setVstyle(s)}
                className={`text-xs px-3 py-1.5 rounded-full border capitalize ${vstyle === s ? 'border-[#8B5CF6] bg-[#8B5CF6]/15 text-violet-100' : 'border-white/12 text-slate-300'}`}>{s}</button>
            ))}
          </div>
          <div className="text-sm text-slate-400 mb-2">Speed — <span className="font-mono text-slate-200">{speed.toFixed(2)}x</span></div>
          <Slider value={[speed]} min={0.8} max={1.2} step={0.05} onValueChange={(v) => setSpeed(v[0])} data-testid="speed-slider" />
          <div className="text-sm text-slate-400 mt-5 mb-2">Emotion — <span className="font-mono text-slate-200">{Math.round(emotion * 100)}%</span></div>
          <Slider value={[emotion]} min={0} max={1} step={0.1} onValueChange={(v) => setEmotion(v[0])} data-testid="emotion-slider" />
        </Card>
      </div>

      <div className="flex items-center gap-3 mt-6">
        <Button data-testid="generate-voice-btn" onClick={generateVoice} disabled={vo.status === 'generating'} className="rounded-full h-11 px-6 font-semibold">
          {vo.status === 'generating' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Mic className="w-4 h-4 mr-2" /> Generate Voice</>}
        </Button>
        {vo.enabled && vo.status === 'ready' && (
          <Button variant="outline" onClick={() => patch('voice/disable', {})} className="rounded-full border-white/15 bg-white/5 hover:bg-white/10">Keep original voice</Button>
        )}
      </div>

      </>)}

      {vo.status === 'ready' && vo.audio_path && (
        <Card className="mt-6">
          <div className="flex items-center gap-2 text-emerald-300 text-sm mb-3"><Check className="w-4 h-4" /> New narration ready ({vo.voice})</div>
          <audio controls src={mediaUrl(id, 'voice', false, vo.generated_at)} className="w-full" data-testid="voice-audio" />
        </Card>
      )}
      <div className="flex justify-end mt-6"><Button onClick={() => setStep('music')} className="rounded-full">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button></div>
    </div>
  );

  const MusicStep = () => {
    const [m, setM] = useState({ enabled: music.enabled ?? true, genre: music.genre || 'cinematic_documentary', mood: music.mood || 'mystery', intensity: music.intensity || 'medium', music_volume: music.music_volume ?? 18, voice_volume: music.voice_volume ?? 100, duck: music.duck ?? true, fade_in: music.fade_in ?? true, fade_out: music.fade_out ?? true });
    const save = (next) => { setM(next); api.patch(`/projects/${id}/music`, next).catch(() => {}); };
    return (
      <div>
        <StepHeader title="Music Studio" desc="Scene-matched background music with automatic ducking under narration." />
        <div className="flex items-center justify-between rounded-xl border border-white/10 bg-[#12141D] px-5 py-4 mb-5">
          <div><div className="font-medium">Background music</div><div className="text-sm text-slate-500">Layer a music bed under your video</div></div>
          <Switch checked={m.enabled} onCheckedChange={(v) => save({ ...m, enabled: v })} data-testid="music-enabled-switch" />
        </div>
        {m.enabled && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="space-y-4">
              {[['Genre', 'genre', GENRES], ['Mood', 'mood', MOODS], ['Intensity', 'intensity', ['low', 'medium', 'high']]].map(([label, key, opts]) => (
                <div key={key}>
                  <div className="text-sm text-slate-400 mb-1.5">{label}</div>
                  <Select value={m[key]} onValueChange={(v) => save({ ...m, [key]: v })}>
                    <SelectTrigger className="bg-[#0B0C12] border-white/12 capitalize" data-testid={`music-${key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{opts.map((o) => <SelectItem key={o} value={o} className="capitalize">{o.replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </Card>
            <Card>
              <div className="text-sm text-slate-400 mb-2">Music volume — <span className="font-mono text-slate-200">{m.music_volume}%</span></div>
              <Slider value={[m.music_volume]} min={0} max={60} step={1} onValueChange={(v) => save({ ...m, music_volume: v[0] })} data-testid="music-volume-slider" />
              <div className="text-sm text-slate-400 mt-5 mb-2">Voice volume — <span className="font-mono text-slate-200">{m.voice_volume}%</span></div>
              <Slider value={[m.voice_volume]} min={0} max={100} step={1} onValueChange={(v) => save({ ...m, voice_volume: v[0] })} data-testid="voice-volume-slider" />
              <div className="space-y-3 mt-6">
                {[['duck', 'Automatically duck music under voice'], ['fade_in', 'Fade in'], ['fade_out', 'Fade out']].map(([k, l]) => (
                  <div key={k} className="flex items-center justify-between"><span className="text-sm text-slate-300">{l}</span><Switch checked={m[k]} onCheckedChange={(v) => save({ ...m, [k]: v })} data-testid={`music-${k}`} /></div>
                ))}
              </div>
            </Card>
          </div>
        )}
        <div className="flex justify-end mt-6"><Button onClick={() => setStep('captions')} className="rounded-full">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button></div>
      </div>
    );
  };

  const CaptionsStep = () => {
    const [c, setC] = useState({ enabled: captions.enabled ?? true, style: captions.style || 'minimal' });
    const save = (next) => { setC(next); api.patch(`/projects/${id}/captions`, next).catch(() => {}); };
    return (
      <div>
        <StepHeader title="Captions" desc="Word-accurate captions burned into the final video." />
        <div className="flex items-center justify-between rounded-xl border border-white/10 bg-[#12141D] px-5 py-4 mb-5">
          <div><div className="font-medium">Auto captions</div><div className="text-sm text-slate-500">Generated from your transcript</div></div>
          <Switch checked={c.enabled} onCheckedChange={(v) => save({ ...c, enabled: v })} data-testid="captions-enabled-switch" />
        </div>
        {c.enabled && (
          <Card>
            <div className="text-sm text-slate-400 mb-3">Style</div>
            <div className="flex gap-2">
              {['minimal', 'bold', 'outline'].map((s) => (
                <button key={s} onClick={() => save({ ...c, style: s })} data-testid={`caption-style-${s}`}
                  className={`text-sm px-4 py-2 rounded-lg border capitalize ${c.style === s ? 'border-[#FF4D00] bg-[#FF4D00]/10' : 'border-white/12 text-slate-300'}`}>{s}</button>
              ))}
            </div>
          </Card>
        )}
        <div className="flex justify-end mt-6"><Button onClick={() => setStep('youtube')} className="rounded-full">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button></div>
      </div>
    );
  };

  const ImagesStep = () => {
    const b = project.broll || {};
    const [style, setStyle] = useState(b.style || 'cinematic');
    const [subject, setSubject] = useState(b.subject || project.title || '');
    const [provider, setProvider] = useState(b.provider || 'nano-banana');
    const STYLES2 = ['cinematic', 'semi-realistic', 'animation', 'documentary', 'watercolor'];
    const scenes = b.scenes || [];
    const gen = async () => {
      await api.post(`/projects/${id}/broll`, { style, subject, provider });
      toast.info('Generating AI scene images…');
      await load(); ensurePolling();
    };

    // custom single-image generator (saved to project assets)
    const [cPrompt, setCPrompt] = useState('');
    const [cProvider, setCProvider] = useState('nano-banana');
    const [cAspect, setCAspect] = useState('youtube');
    const [cBusy, setCBusy] = useState(false);
    const [customs, setCustoms] = useState([]);
    const loadCustoms = async () => {
      try { const { data } = await api.get(`/images?kind=scene&project_id=${id}`); setCustoms(data); } catch { /* ignore */ }
    };
    useEffect(() => { loadCustoms(); /* eslint-disable-next-line */ }, []);
    const genCustom = async () => {
      if (cPrompt.trim().length < 3) { toast.error('Describe the image you want'); return; }
      setCBusy(true);
      try {
        await api.post('/images/generate', { prompt: cPrompt.trim(), provider: cProvider, aspect: cAspect, style, kind: 'scene', project_id: id });
        toast.success('Image generated'); setCPrompt(''); loadCustoms();
      } catch (e) { toast.error(e?.response?.data?.detail || 'Generation failed'); }
      finally { setCBusy(false); }
    };
    const delCustom = async (aid) => { await api.delete(`/images/${aid}`); loadCustoms(); };

    return (
      <div>
        <StepHeader title="AI Images & B-roll" desc="Generate a consistent image for every scene and sequence them as B-roll synced to the narration." />
        <Card className="mb-5">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-slate-400 mb-1.5">Subject / theme</div>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="bg-[#0B0C12] border-white/12" data-testid="broll-subject" placeholder="e.g. Lost city of Atlantis" />
            </div>
            <div>
              <div className="text-sm text-slate-400 mb-1.5">Visual style</div>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger className="bg-[#0B0C12] border-white/12 capitalize" data-testid="broll-style"><SelectValue /></SelectTrigger>
                <SelectContent>{STYLES2.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-sm text-slate-400 mb-1.5">Image model</div>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger className="bg-[#0B0C12] border-white/12" data-testid="broll-provider"><SelectValue /></SelectTrigger>
                <SelectContent>{IMG_PROVIDERS.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`broll-provider-${p.id}`}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button data-testid="generate-broll-btn" onClick={gen} disabled={b.status === 'generating'} className="rounded-full mt-4 font-semibold">
            {b.status === 'generating' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {b.progress || 'Generating…'}</> : <><ImageIcon className="w-4 h-4 mr-2" /> {scenes.length ? 'Regenerate images' : 'Generate scene images'}</>}
          </Button>
        </Card>

        {b.status === 'ready' && scenes.length > 0 && (
          <>
            <div className="flex items-center justify-between rounded-xl border border-white/10 bg-[#12141D] px-5 py-4 mb-4">
              <div><div className="font-medium">Use images as the video</div><div className="text-sm text-slate-500">Replace footage with a B-roll slideshow synced to narration</div></div>
              <Switch checked={!!b.enabled} onCheckedChange={(v) => patch('broll/toggle', { enabled: v })} data-testid="broll-enabled-switch" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {scenes.filter((s) => s.image_path).map((s) => (
                <div key={s.index} className="rounded-xl overflow-hidden border border-white/10 bg-[#0B0C12]" data-testid={`broll-scene-${s.index}`}>
                  <img src={imageUrl(id, s.index, b.generated_at)} alt="" className="w-full aspect-video object-cover" />
                  <div className="px-2 py-1.5 font-mono text-[10px] text-slate-500 truncate">{fmt(s.start)} · {(s.prompt || '').slice(0, 42)}</div>
                </div>
              ))}
            </div>
          </>
        )}
        {b.status === 'failed' && <Card className="border-rose-500/30"><div className="text-rose-300 text-sm">Image generation failed. Please try again.</div></Card>}

        <Card className="mt-6">
          <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-[#FF884D]" /><div className="font-heading text-lg font-bold">Custom image from a prompt</div></div>
          <div className="text-sm text-slate-500 mb-4">Describe any extra visual you need — a diagram, an establishing shot, an insert. Saved to this project.</div>
          <Textarea value={cPrompt} onChange={(e) => setCPrompt(e.target.value)} data-testid="custom-image-prompt" placeholder="A slow aerial shot over a misty mountain range at dawn…" className="bg-[#0B0C12] border-white/12 min-h-[80px]" />
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <Select value={cProvider} onValueChange={setCProvider}>
              <SelectTrigger className="bg-[#0B0C12] border-white/12" data-testid="custom-image-provider"><SelectValue /></SelectTrigger>
              <SelectContent>{IMG_PROVIDERS.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`custom-provider-${p.id}`}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={cAspect} onValueChange={setCAspect}>
              <SelectTrigger className="bg-[#0B0C12] border-white/12" data-testid="custom-image-aspect"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="youtube">16:9 Widescreen</SelectItem>
                <SelectItem value="square">1:1 Square</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="generate-custom-image-btn" onClick={genCustom} disabled={cBusy} className="rounded-full mt-4 font-semibold">
            {cBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><ImageIcon className="w-4 h-4 mr-2" /> Generate image</>}
          </Button>
          {customs.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
              {customs.map((a) => (
                <div key={a.id} className="rounded-xl overflow-hidden border border-white/10 bg-[#0B0C12] group relative" data-testid={`custom-image-${a.id}`}>
                  <img src={assetUrl(a.id, a.created_at)} alt="" className={`w-full object-cover ${a.aspect === 'square' ? 'aspect-square' : 'aspect-video'}`} />
                  <button onClick={() => delCustom(a.id)} data-testid={`delete-custom-${a.id}`} className="absolute top-1.5 right-1.5 grid place-items-center w-7 h-7 rounded-lg bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-600"><X className="w-4 h-4" /></button>
                  <div className="px-2 py-1.5 font-mono text-[10px] text-slate-500 truncate">{a.provider} · {(a.prompt || '').slice(0, 40)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="flex justify-end mt-6"><Button onClick={() => setStep('captions')} className="rounded-full">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button></div>
      </div>
    );
  };

  const YoutubeStep = () => {
    const yt = project.youtube || {};
    const [gen, setGen] = useState(false);
    const [local, setLocal] = useState(yt);
    useEffect(() => { setLocal(project.youtube || {}); }, [project.youtube]);
    const generate = async () => {
      setGen(true);
      try { const { data } = await api.post(`/projects/${id}/youtube`); setLocal(data); load(); toast.success('YouTube package generated'); }
      catch { toast.error('Generation failed'); } finally { setGen(false); }
    };
    const save = () => { api.patch(`/projects/${id}/youtube`, local); toast.success('Saved'); };
    const has = local && local.title;
    return (
      <div>
        <StepHeader title="YouTube Package" desc="AI-generated title, description, chapters, tags and thumbnail concept." />
        {!has ? (
          <Card className="text-center py-10">
            <Youtube className="w-10 h-10 text-[#FF4D00] mx-auto mb-4" />
            <div className="text-slate-300 mb-5">Generate your complete YouTube upload package.</div>
            <Button data-testid="generate-youtube-btn" onClick={generate} disabled={gen} className="rounded-full">
              {gen ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Package</>}
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            <Card>
              <div className="text-sm text-slate-400 mb-1.5">Title</div>
              <Input value={local.title || ''} onChange={(e) => setLocal({ ...local, title: e.target.value })} className="bg-[#0B0C12] border-white/12" data-testid="yt-title" />
              <div className="text-sm text-slate-400 mb-1.5 mt-4">Description</div>
              <Textarea rows={5} value={local.description || ''} onChange={(e) => setLocal({ ...local, description: e.target.value })} className="bg-[#0B0C12] border-white/12" data-testid="yt-description" />
            </Card>
            <div className="grid lg:grid-cols-2 gap-4">
              <Card>
                <div className="text-sm text-slate-400 mb-2">Chapters</div>
                <div className="space-y-1.5 font-mono text-sm">
                  {(local.chapters || []).map((ch, i) => <div key={i} className="flex gap-3"><span className="text-[#FF884D]">{ch.time}</span><span className="text-slate-300">{ch.label}</span></div>)}
                </div>
              </Card>
              <Card>
                <div className="text-sm text-slate-400 mb-2">Tags</div>
                <div className="flex flex-wrap gap-1.5">{(local.tags || []).map((t) => <Badge key={t} variant="outline" className="border-white/12 text-slate-300">{t}</Badge>)}</div>
              </Card>
            </div>
            <Card>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><div className="text-sm text-slate-400 mb-1">Thumbnail concept</div><div className="text-sm text-slate-200">{local.thumbnail_concept}</div></div>
                <div><div className="text-sm text-slate-400 mb-1">Thumbnail text</div><div className="font-heading text-2xl font-extrabold text-gradient">{local.thumbnail_text}</div></div>
              </div>
              <div className="mt-4"><div className="text-sm text-slate-400 mb-1">Pinned comment</div><div className="text-sm text-slate-300">{local.pinned_comment}</div></div>
            </Card>
            <div className="flex gap-3">
              <Button onClick={save} className="rounded-full" data-testid="save-youtube-btn">Save changes</Button>
              <Button variant="outline" onClick={generate} disabled={gen} className="rounded-full border-white/15 bg-white/5 hover:bg-white/10"><RefreshCw className="w-4 h-4 mr-1" /> Regenerate</Button>
            </div>
          </div>
        )}
        <div className="flex justify-end mt-6"><Button onClick={() => setStep('export')} className="rounded-full">Continue to export <ArrowRight className="w-4 h-4 ml-1" /></Button></div>
      </div>
    );
  };

  const ExportStep = () => {
    const allowedRes = user?.plan === 'free' ? ['720p'] : user?.plan === 'creator' ? ['720p', '1080p'] : ['720p', '1080p', '4k'];
    const [res, setRes] = useState(exp.resolution || '1080p');
    const [format, setFormat] = useState(exp.format || '16:9');
    const [fps, setFps] = useState(exp.fps || 30);
    const rendering = ['queued', 'rendering'].includes(exp.status);
    const startExport = async () => {
      if (!allowedRes.includes(res)) { toast.error(`${res} requires a higher plan`); return; }
      try { await api.post(`/projects/${id}/export`, { resolution: res, format, fps }); toast.info('Rendering your video…'); await load(); ensurePolling(); refresh(); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Export failed'); }
    };
    return (
      <div>
        <StepHeader title="Export Video" desc="Render a real, downloadable edited video." />
        <div className="grid lg:grid-cols-3 gap-4">
          <Card>
            <div className="text-sm text-slate-400 mb-3">Resolution</div>
            {['720p', '1080p', '4k'].map((r) => (
              <button key={r} data-testid={`res-${r}`} onClick={() => setRes(r)} disabled={!allowedRes.includes(r)}
                className={`w-full flex items-center justify-between rounded-lg border px-4 py-2.5 mb-2 text-sm ${res === r ? 'border-[#FF4D00] bg-[#FF4D00]/10' : 'border-white/10 bg-[#0B0C12]'} ${!allowedRes.includes(r) ? 'opacity-40 cursor-not-allowed' : ''}`}>
                {r.toUpperCase()} {!allowedRes.includes(r) && <span className="text-[10px] font-mono text-slate-500">Upgrade</span>}
              </button>
            ))}
          </Card>
          <Card>
            <div className="text-sm text-slate-400 mb-3">Format</div>
            {[['16:9', 'YouTube 16:9'], ['9:16', 'Shorts 9:16'], ['1:1', 'Square 1:1']].map(([v, l]) => (
              <button key={v} data-testid={`fmt-${v}`} onClick={() => setFormat(v)}
                className={`w-full rounded-lg border px-4 py-2.5 mb-2 text-sm text-left ${format === v ? 'border-[#FF4D00] bg-[#FF4D00]/10' : 'border-white/10 bg-[#0B0C12]'}`}>{l}</button>
            ))}
          </Card>
          <Card>
            <div className="text-sm text-slate-400 mb-3">Frame rate</div>
            <Select value={String(fps)} onValueChange={(v) => setFps(Number(v))}>
              <SelectTrigger className="bg-[#0B0C12] border-white/12" data-testid="fps-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[24, 30, 60].map((f) => <SelectItem key={f} value={String(f)}>{f} fps</SelectItem>)}</SelectContent>
            </Select>
            <div className="mt-4 text-xs font-mono text-slate-500">Audio · AAC 48kHz</div>
          </Card>
        </div>

        <Button data-testid="export-btn" onClick={startExport} disabled={rendering} className="rounded-full h-12 px-8 mt-6 font-semibold text-base">
          {rendering ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {exp.progress || 'Rendering…'}</> : <><Download className="w-4 h-4 mr-2" /> Export Video</>}
        </Button>

        {exp.status === 'failed' && <Card className="mt-6 border-rose-500/30"><div className="text-rose-300 text-sm">Render failed: {exp.error}</div></Card>}

        {exp.status === 'done' && exp.output_path && (
          <Card className="mt-6">
            <div className="flex items-center gap-2 text-emerald-300 text-sm mb-4"><Check className="w-4 h-4" /> Render complete — {fmt(exp.output_duration)} · {(exp.output_size / 1024 / 1024).toFixed(1)} MB</div>
            <video controls src={mediaUrl(id, 'output', false, exp.rendered_at)} className="w-full aspect-video bg-black rounded-xl" data-testid="output-video" />
            <div className="flex flex-wrap gap-3 mt-5">
              <a href={mediaUrl(id, 'output', true, exp.rendered_at)} data-testid="download-btn"><Button className="rounded-full"><Download className="w-4 h-4 mr-2" /> Download</Button></a>
              <Button variant="outline" onClick={() => { setFormat('9:16'); toast.info('Set to 9:16 — export to create a Short'); }} className="rounded-full border-white/15 bg-white/5 hover:bg-white/10">Create Short</Button>
              <Button variant="outline" onClick={() => setStep('edit')} className="rounded-full border-white/15 bg-white/5 hover:bg-white/10">Create another version</Button>
            </div>
          </Card>
        )}
      </div>
    );
  };

  const CONTENT = { overview: Overview, edit: EditStep, transcript: TranscriptStep, voice: VoiceStep, music: MusicStep, images: ImagesStep, captions: CaptionsStep, youtube: YoutubeStep, export: ExportStep };
  const Current = CONTENT[step];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="min-w-0">
          <button onClick={() => nav('/app/projects')} className="text-xs text-slate-500 hover:text-white font-mono">← Projects</button>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight truncate">{project.title}</h1>
        </div>
        <Badge variant="outline" className="border-white/12 text-slate-300 font-mono shrink-0"><Clock className="w-3 h-3 mr-1" /> {fmt(a.duration)}</Badge>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
          {STEPS.map((s) => {
            const done = (s.key === 'edit' && edit.status === 'ready') || (s.key === 'voice' && vo.status === 'ready') || (s.key === 'export' && exp.status === 'done');
            return (
              <button key={s.key} data-testid={`step-${s.key}`} onClick={() => setStep(s.key)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${step === s.key ? 'bg-[#FF4D00]/12 text-white border border-[#FF4D00]/30' : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'}`}>
                <s.icon className="w-4 h-4" /> {s.label}
                {done && <Check className="w-3.5 h-3.5 text-emerald-400 ml-auto" />}
              </button>
            );
          })}
        </div>
        <div className="min-w-0"><Current /></div>
      </div>
    </div>
  );
}
